/**
 * Number and date formatting for EVERY lottery page - one place, so all of
 * them read the same way.
 *
 * The business reads lakhs and crores, so counts use Indian digit grouping
 * (18,76,116 - not 1,876,116) and money is rupees with the ₹ sign.
 * The upload pages CALCULATE their percentages from counts
 * (formatUnsoldPercent); the Lottery page shows the reporting layer's own
 * percentages (formatPercent, formatSignedPercent), which rpt_* already
 * rounds to 1 decimal - so the page, the reports and the chatbot agree.
 */

const COUNT = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const RUPEES = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const PERCENT = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** 1876116 -> "18,76,116". */
export function formatCount(value: number): string {
  return COUNT.format(value);
}

/** 1704 -> "₹1,704"; 1234567.5 -> "₹12,34,567.5". */
export function formatRupees(value: number): string {
  return RUPEES.format(value);
}

/** unsold / despatched as "12.5%"; "–" when nothing was despatched. */
export function formatUnsoldPercent(unsold: number, despatched: number): string {
  if (!despatched) return '–';
  return `${PERCENT.format((unsold / despatched) * 100)}%`;
}

/** A signed change: "+1,204" / "−310" / "0". */
export function formatChange(value: number): string {
  if (value === 0) return '0';
  return `${value > 0 ? '+' : '−'}${COUNT.format(Math.abs(value))}`;
}

/** Change as a percentage of `before`: "+4.2%"; "–" without a base. */
export function formatChangePercent(after: number, before: number): string {
  if (!before) return '–';
  const pct = ((after - before) / before) * 100;
  return `${pct > 0 ? '+' : pct < 0 ? '−' : ''}${PERCENT.format(Math.abs(pct))}%`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "2026-09-28" -> "Mon, 28 Sep 2026" - the sale date itself (no timezone
 * shift), built from fixed names so it reads the same in every browser
 * (ICU's en-IN output differs between versions: "Sept", extra commas).
 */
export function formatSaleDate(date: string | null): string {
  if (!date) return 'Unknown date';
  const [y, m, d] = date.split('-').map(Number);
  const weekday = new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay();
  return `${WEEKDAYS[weekday]}, ${d} ${MONTHS[m! - 1]} ${y}`;
}

/** A timestamp in the viewer's time: "28 Sep 2026, 3:05 pm". */
export function formatWhen(iso: string | null): string {
  if (!iso) return '–';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
}

/** A percentage from the reporting layer: 12.5 -> "12.5%"; "–" when there is none. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '–';
  return `${PERCENT.format(value)}%`;
}

/** A change from the reporting layer: 4.2 -> "+4.2%", -3.1 -> "−3.1%"; "–" when there is none. */
export function formatSignedPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '–';
  if (value === 0) return '0.0%';
  return `${value > 0 ? '+' : '−'}${PERCENT.format(Math.abs(value))}%`;
}

/** File sizes: "84 KB", "1.2 MB". */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** "2026-09-28" -> "28-Sep-26" (the old workbooks' week labels). */
export function formatShortDate(date: string | null): string {
  if (!date) return '–';
  const [y, m, d] = date.split('-');
  return `${d}-${MONTHS[Number(m) - 1]}-${y!.slice(2)}`;
}

/** Chart axes: 1,23,45,678 -> "1.2 Cr", 18,76,116 -> "18.8 L", smaller -> Indian grouping. */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 10_000_000) return `${(value / 10_000_000).toFixed(1)} Cr`;
  if (abs >= 100_000) return `${(value / 100_000).toFixed(1)} L`;
  return COUNT.format(value);
}

/**
 * One value of a Lottery analyst table or chart (Phase 8), by the kind the
 * server gave its column: counts in Indian grouping, ₹, percentages (1 or
 * 2 decimals), dates as "Mon, 28 Sep 2026", "–" for nothing.
 */
export function formatLotteryCell(
  value: string | number | null | undefined,
  kind: 'text' | 'date' | 'count' | 'money' | 'pct1' | 'pct2' | 'int',
): string {
  if (value === null || value === undefined || value === '') return '–';
  if (kind === 'date' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatSaleDate(value);
  if (typeof value !== 'number') return String(value);
  switch (kind) {
    case 'count': return formatCount(value);
    case 'money': return formatRupees(value);
    case 'pct1': return formatPercent(value);
    case 'pct2': return `${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}%`;
    case 'int': return Number.isInteger(value) ? String(value) : formatCount(value);
    default: return String(value);
  }
}
